import React, { useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { UPDATE_PROJECT } from '../../graphql/mutations';
import { showInkToast } from '../cms/InkToast';
import {
  CAMPAIGN_COLORS,
  CampaignHue,
  campaignColorName,
  campaignHue,
  hueVar,
} from './campaignColors';

/**
 * The color square next to a campaign's name on its page (TinyCMS UX
 * optimization review, 2026-10-24). Clicking it opens the eight colors and a
 * note; picking one saves it for everyone on the site. Without `canChange`
 * (Campaign-role people) it's just the square, named on hover.
 */
export function CampaignColorPicker({
  campaign,
  canChange,
  onChanged,
}: {
  campaign: { id: string; color?: string | null };
  canChange: boolean;
  onChanged?: () => void;
}) {
  const hue = campaignHue(campaign);
  const [open, setOpen] = useState(false);
  // Shown at once, then confirmed by the refetch.
  const [picked, setPicked] = useState<CampaignHue | null>(null);
  const shown = picked || hue;
  const box = useRef<HTMLSpanElement>(null);
  const [save] = useMutation(UPDATE_PROJECT);

  useEffect(() => setPicked(null), [campaign.id, campaign.color]);

  useEffect(() => {
    if (!open) return undefined;
    const outside = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  const pick = async (key: CampaignHue) => {
    setOpen(false);
    if (key === shown) return;
    setPicked(key);
    try {
      await save({ variables: { id: campaign.id, input: { color: key } } });
      onChanged?.();
    } catch (err) {
      setPicked(null);
      showInkToast(err instanceof Error ? err.message : 'The color didn’t save. Try again.');
    }
  };

  const name = campaignColorName(shown);
  if (!canChange) {
    return (
      <span
        className="ccp-chip"
        style={{ background: hueVar(shown) }}
        title={`Campaign color: ${name}`}
      />
    );
  }
  return (
    <span className="ccp" ref={box}>
      <button
        type="button"
        className="ccp-chip ccp-chip-button"
        style={{ background: hueVar(shown) }}
        title={`Campaign color: ${name}`}
        aria-label="Change campaign color"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      />
      {open ? (
        <div className="ccp-pop" role="dialog" aria-label="Campaign color">
          <div className="ccp-head">
            Campaign color <span>{name}</span>
          </div>
          <div className="ccp-swatches">
            {CAMPAIGN_COLORS.map(([key, label]) => (
              <button
                type="button"
                key={key}
                className="ccp-swatch"
                data-on={key === shown ? 'true' : undefined}
                style={{ background: hueVar(key) }}
                title={label}
                aria-label={label}
                aria-pressed={key === shown}
                onClick={() => void pick(key)}
              />
            ))}
          </div>
          <p className="ccp-note">
            Colors this campaign’s bar on Today and in the calendar. Everyone on the site sees the
            same color.
          </p>
        </div>
      ) : null}
    </span>
  );
}
